import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Row, Col } from 'react-styled-flexboxgrid';

import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { Button, H2, IconButton, Modal, TextField, Label } from '../../../../../components';

const ModalHeaderWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  height: 40px;
`;

const ActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  column-gap: 10px;
`;

const StyledInput = styled(TextField)`
  width: 100%;
  min-width: 200px;
  margin-bottom: 0;
`;

const StyledLabel = styled(Label)`
  font-weight: 600;
  color: var(--content-title);
  margin-bottom: 2px;
`;

const stripeSchema = yup.object().shape({
  apiKey: yup.string().required('Enter API key')
});

const dbmsSchema = yup.object().shape({
  host: yup.string().required('Enter host'),
  port: yup.number().typeError('Port must be a valid number').required('Enter port number'),
  username: yup.string().required('Enter username'),
  password: yup.string().required('Enter password'),
  database: yup.string().required('Enter database')
});

const CustomSourceModal = ({ integration, handleCreateSource }) => {
  const stripeForm = useForm({
    resolver: yupResolver(stripeSchema),
    mode: 'onSubmit'
  });

  const dbmsForm = useForm({
    resolver: yupResolver(dbmsSchema),
    mode: 'onSubmit'
  });

  const handleStripeFormSubmit = useCallback(
    (data, close) => {
      handleCreateSource(data.apiKey, integration.id, 'custom');
      close();
    },
    [integration, handleCreateSource]
  );

  const handleDbmsFormSubmit = useCallback(
    (data, close) => {
      handleCreateSource(data, integration.id, 'custom');
      close();
    },
    [integration, handleCreateSource]
  );

  const renderStripeForm = (close) => (
    <form
      onSubmit={stripeForm.handleSubmit((data) => handleStripeFormSubmit(data, close))}
      noValidate
    >
      <Row>
        <Col xs={12}>
          <StyledLabel $small htmlFor='apiKey'>
            API key
          </StyledLabel>
          <StyledInput
            placeholder='Read-only API key'
            {...stripeForm.register('apiKey')}
            error={stripeForm.formState.errors.apiKey?.message}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          <ActionsWrapper>
            <Button naked onClick={close}>
              Back
            </Button>
            <Button primary type='submit'>
              Save
            </Button>
          </ActionsWrapper>
        </Col>
      </Row>
    </form>
  );

  const renderDbmsForm = (close) => (
    <form onSubmit={dbmsForm.handleSubmit((data) => handleDbmsFormSubmit(data, close))} noValidate>
      <Row>
        <Col xs={8}>
          <StyledLabel $small htmlFor='host'>
            Host
          </StyledLabel>
          <StyledInput
            placeholder='Host'
            {...dbmsForm.register('host')}
            error={dbmsForm.formState.errors.host?.message}
          />
        </Col>
        <Col xs={2}>
          <StyledLabel $small htmlFor='port'>
            Port
          </StyledLabel>
          <StyledInput
            placeholder='Port'
            {...dbmsForm.register('port')}
            error={dbmsForm.formState.errors.port?.message}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={6}>
          <StyledLabel $small htmlFor='username'>
            Username
          </StyledLabel>
          <StyledInput
            placeholder='Username'
            {...dbmsForm.register('username')}
            error={dbmsForm.formState.errors.username?.message}
          />
        </Col>
        <Col xs={6}>
          <StyledLabel $small htmlFor='password'>
            Password
          </StyledLabel>
          <StyledInput
            placeholder='Password'
            {...dbmsForm.register('password')}
            error={dbmsForm.formState.errors.password?.message}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          <StyledLabel $small htmlFor='database'>
            Database name
          </StyledLabel>
          <StyledInput
            placeholder='Database name'
            {...dbmsForm.register('database')}
            error={dbmsForm.formState.errors.database?.message}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          <ActionsWrapper>
            <Button naked onClick={close}>
              Back
            </Button>
            <Button primary type='submit'>
              Save
            </Button>
          </ActionsWrapper>
        </Col>
      </Row>
    </form>
  );

  return (
    <Modal size='md' bodyStyle={{ padding: 20 }}>
      {(close) => (
        <>
          <ModalHeaderWrapper>
            <H2>Enter your {integration.displayName} credentials</H2>
            <IconButton small naked iconName='close' onClick={close} iconDisplaySize={24} />
          </ModalHeaderWrapper>
          {integration.name.includes('stripe') && renderStripeForm(close)}
          {integration.name.includes('direct_db') && renderDbmsForm(close)}
        </>
      )}
    </Modal>
  );
};

CustomSourceModal.propTypes = {
  integration: PropTypes.shape({
    id: PropTypes.string,
    displayName: PropTypes.string,
    name: PropTypes.string
  }).isRequired,
  handleCreateSource: PropTypes.func.isRequired
};

export { CustomSourceModal };
